'use client';

import { c as _c } from "react/compiler-runtime";
import { QueryClient, QueryKey, UseMutationOptions, UseQueryResult, useIsFetching, useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@sola/auth-sdk/store.tsx';
import { MOCK_SUPPORTER, QUERY_KEYS } from '../constants.ts';
export const useReadyForQueries = () => {
  const $ = _c(3);
  const {
    authUser
  } = useAuth("authUser");
  const t0 = !!MOCK_SUPPORTER || !!authUser?.authUserGlobalId;
  const t1 = authUser?.authUserGlobalId;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = {
      isReady: t0,
      globalId: t1
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const useDataPrimer = () => {
  const $ = _c(5);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: [QUERY_KEYS.dataPrimerReadOnly]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useIsFetching(t0);
  let t1;
  if ($[1] !== queryClient) {
    t1 = queryClient.getQueryState([QUERY_KEYS.dataPrimerReadOnly]);
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const primerState = t1;
  const t2 = !!primerState && primerState.status !== "pending" && primerState.fetchStatus !== "fetching";
  let t3;
  if ($[3] !== t2) {
    t3 = {
      isPrimerReady: t2
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
type AnySupporterQuery<TData = unknown> = {
  queryKey?: QueryKey;
  queryFn: (globalId: string, queryContext?: {
    queryClient: QueryClient;
  }) => Promise<TData>;
  waitForPrimer?: boolean;
};
type InferSupporterQueryData<TQuery extends AnySupporterQuery> = Awaited<ReturnType<TQuery['queryFn']>>;
type SupporterQueryTupleData<TQueries extends readonly AnySupporterQuery[]> = { [K in keyof TQueries]: TQueries[K] extends AnySupporterQuery ? InferSupporterQueryData<TQueries[K]> | undefined : never };
type SupporterQueryTupleResult<TQueries extends readonly AnySupporterQuery[]> = {
  data: SupporterQueryTupleData<TQueries>;
  isFetched: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isPending: boolean;
  isPaused: boolean;
  isError: boolean;
  isSuccess: boolean;
  queries: ReturnType<typeof useQueries>;
};
export function useSupporterQuery<TQuery extends AnySupporterQuery>(supporterQueries: TQuery): UseQueryResult<InferSupporterQueryData<TQuery>>;
export function useSupporterQuery<TQueries extends readonly AnySupporterQuery[]>(supporterQueries: TQueries): SupporterQueryTupleResult<TQueries>;
export function useSupporterQuery(supporterQueries) {
  const $ = _c(41);
  const {
    isReady,
    globalId
  } = useReadyForQueries();
  const {
    isPrimerReady
  } = useDataPrimer();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== supporterQueries) {
    t0 = Array.isArray(supporterQueries) ? supporterQueries : [supporterQueries];
    $[0] = supporterQueries;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queries = t0;
  let t1;
  if ($[2] !== globalId || $[3] !== isPrimerReady || $[4] !== isReady || $[5] !== queries || $[6] !== queryClient) {
    let t2;
    if ($[8] !== globalId || $[9] !== isPrimerReady || $[10] !== isReady || $[11] !== queryClient) {
      t2 = supporterQuery => ({
        queryKey: supporterQuery.queryKey,
        queryFn: () => supporterQuery.queryFn(globalId as string, {
          queryClient
        }),
        enabled: !!isReady && (!supporterQuery.waitForPrimer || isPrimerReady),
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retryOnMount: false
      });
      $[8] = globalId;
      $[9] = isPrimerReady;
      $[10] = isReady;
      $[11] = queryClient;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    t1 = queries.map(t2);
    $[2] = globalId;
    $[3] = isPrimerReady;
    $[4] = isReady;
    $[5] = queries;
    $[6] = queryClient;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[13] !== t1) {
    t2 = {
      queries: t1
    };
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const queryResults = useQueries(t2);
  if (!Array.isArray(supporterQueries)) {
    return queryResults[0] as UseQueryResult<unknown>;
  }
  let t3;
  if ($[15] !== queryResults) {
    t3 = queryResults.map(_temp);
    $[15] = queryResults;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== queryResults) {
    t4 = queryResults.every(_temp2);
    $[17] = queryResults;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== queryResults) {
    t5 = queryResults.some(_temp3);
    $[19] = queryResults;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== queryResults) {
    t6 = queryResults.some(_temp4);
    $[21] = queryResults;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== queryResults) {
    t7 = queryResults.some(_temp5);
    $[23] = queryResults;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== queryResults) {
    t8 = queryResults.some(_temp6);
    $[25] = queryResults;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== queryResults) {
    t9 = queryResults.some(_temp7);
    $[27] = queryResults;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== queryResults) {
    t10 = queryResults.every(_temp8);
    $[29] = queryResults;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== queryResults || $[32] !== t10 || $[33] !== t3 || $[34] !== t4 || $[35] !== t5 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t11 = {
      data: t3,
      isFetched: t4,
      isFetching: t5,
      isLoading: t6,
      isPending: t7,
      isPaused: t8,
      isError: t9,
      isSuccess: t10,
      queries: queryResults
    };
    $[31] = queryResults;
    $[32] = t10;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  return t11 as SupporterQueryTupleResult<readonly AnySupporterQuery[]>;
}
function _temp8(result_6) {
  return result_6.isSuccess;
}
function _temp7(result_5) {
  return result_5.isError;
}
function _temp6(result_4) {
  return result_4.isPaused;
}
function _temp5(result_3) {
  return result_3.isPending;
}
function _temp4(result_2) {
  return result_2.isLoading;
}
function _temp3(result_1) {
  return result_1.isFetching;
}
function _temp2(result_0) {
  return result_0.isFetched;
}
function _temp(result) {
  return result.data;
}
export const useSupporterMutation = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = !!MOCK_SUPPORTER && {
      mutationFn: _temp10,
      onSuccess: _temp11
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      ...options,
      ...t0
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
function _temp9(resolve) {
  return setTimeout(resolve, 1000);
}
async function _temp10() {
  await new Promise(_temp9);
  return true as TData;
}
function _temp11() {
  return alert("Success!");
}